import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label: string;
  /**
   * Renders `label` visibly above the field, not only as its accessible name.
   * Opt-in: member screens supply their own labels from the Figma designs, so
   * defaulting this on would add a second label to every existing field.
   */
  showLabel?: boolean;
  icon?: ReactNode;
  /** Static adornment rendered inside the field before the value (e.g. "$"). */
  prefix?: ReactNode;
  /** Static adornment rendered inside the field after the value (e.g. a lock icon). */
  rightIcon?: ReactNode;
  /**
   * Confirmed, non-editable value shown with a green border and full-colour
   * text (NOT the dimmed `disabled` look) - e.g. the pre-filled, verified
   * recipient email on the shared-profile verify screen (MYN-58). Implies
   * readOnly.
   */
  locked?: boolean;
  /**
   * `bare` drops the border/padding/ring so the input can live inside a
   * composite field shell (e.g. the inset-label money fields, Figma 287:3098).
   */
  variant?: 'default' | 'bare';
  error?: string;
}

// Explicit white field surface (`bg-surface-card`, the same token `Select`
// uses) so an input inside a tinted container (e.g. the wizard's co-founder
// card) does not pick up the tint and read differently from the Select beside
// it (UX sheet r126). `disabled:` and `admin-dark:` variants are emitted after
// the base utility, so the disabled tint and the dark-theme surface still win.
const INPUT_SURFACE =
  'bg-surface-card text-text-primary placeholder:text-input-placeholder disabled:border-teal/5 disabled:bg-teal/5 ' +
  'admin-dark:bg-surface-page admin-dark:placeholder:text-text-muted ' +
  'admin-dark:disabled:border-border-subtle admin-dark:disabled:bg-surface-card';

// Resting border - error/locked overrides (below) always win over this.
const INPUT_BORDER = 'border-text-muted admin-dark:border-border-strong';

/**
 * Input types whose native picker browsers only open from the small calendar/
 * clock glyph at the right-hand edge.
 */
const PICKER_TYPES = new Set(['date', 'datetime-local', 'month', 'time', 'week']);

/**
 * Open a date/time input's native picker from a click anywhere in the field,
 * not just its glyph (client feedback 2026-08-20 - the admin date filters read
 * as broken, because the whole field looks clickable and only ~16px of it was).
 *
 * No try/catch, and none needed: `showPicker` throws only without user
 * activation, on a detached element, or across origins. This runs from a click
 * handler on a mounted, same-document input, so none of those can occur - and
 * the guards below cover the rest (a browser without the method, and a field
 * that must not be edited at all).
 */
function openPickerOnClick(event: MouseEvent<HTMLInputElement>): void {
  const field = event.currentTarget;
  if (!PICKER_TYPES.has(field.type)) return;
  if (field.disabled || field.readOnly) return;
  if (typeof field.showPicker !== 'function') return;
  field.showPicker();
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    showLabel = false,
    icon,
    prefix,
    rightIcon,
    locked = false,
    variant = 'default',
    error,
    className = '',
    ...props
  },
  ref,
) {
  const errorId = useId();
  const fieldId = useId();
  const hasError = Boolean(error);
  // A picker field is clickable across its whole width now, so it must say so
  // (mnr-standards design checklist item 8: pointer on all clickables). A
  // locked/readOnly one opens nothing, so it keeps the default cursor.
  const isClickablePicker =
    props.type !== undefined &&
    PICKER_TYPES.has(props.type) &&
    !locked &&
    !props.readOnly &&
    !props.disabled;

  if (variant === 'bare') {
    return (
      <input
        ref={ref}
        aria-label={label}
        aria-invalid={hasError}
        className={`w-full border-none bg-transparent p-0 text-input text-text-primary placeholder:text-input-placeholder focus:outline-none ${className}`.trim()}
        {...props}
      />
    );
  }

  // Border precedence: an error always wins; a locked/confirmed field shows
  // green; otherwise the surface's neutral resting border.
  const borderClass = hasError ? 'border-red' : locked ? 'border-green' : INPUT_BORDER;
  // A locked field is static (readOnly, no focus state); editable fields get
  // the guideline's Focus State border color (§4: Green Primary, not Green Dark).
  const focusClass = locked ? '' : 'focus:outline-none focus:border-green';

  return (
    <div className="flex flex-col gap-1">
      {showLabel && (
        <label htmlFor={fieldId} className="text-text-primary text-p6 font-medium">
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-input-placeholder">
            {icon}
          </span>
        )}
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm text-text-primary">
            {prefix}
          </span>
        )}
        {rightIcon && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-input-placeholder">
            {rightIcon}
          </span>
        )}
        <input
          ref={ref}
          id={showLabel ? fieldId : undefined}
          aria-label={label}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : undefined}
          readOnly={locked || props.readOnly}
          className={`w-full border ${borderClass} rounded-input py-2 ${icon ? 'pl-10' : prefix ? 'pl-7' : 'pl-4'} ${rightIcon ? 'pr-10' : 'pr-4'} text-input disabled:cursor-not-allowed ${isClickablePicker ? 'cursor-pointer' : ''} ${INPUT_SURFACE} ${focusClass} ${className}`.trim()}
          {...props}
          /*
            AFTER the spread, so `openPickerOnClick` always runs, then a caller's
            own handler. Declared before the spread it would be silently replaced
            by any `onClick` a call site passes - and this is the kind of default
            that must not depend on which props a caller happens to set. A no-op
            for every non-picker input type.
          */
          onClick={(event) => {
            openPickerOnClick(event);
            props.onClick?.(event);
          }}
        />
      </div>
      {hasError && (
        <p id={errorId} role="alert" className="text-xs text-red pl-1">
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
